import { Link } from 'react-router-dom';
import { companyInfo } from '@/mocks/home';
import { getGenericWhatsAppUrl } from '@/utils/whatsapp';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const quickLinks = [
  { label: 'Home', path: '/' },
  { label: 'About Us', path: '/about' },
  { label: 'Services', path: '/services' },
  { label: 'Projects', path: '/projects' },
  { label: 'Testimonials', path: '/testimonials' },
  { label: 'Blog', path: '/blog' },
  { label: 'Contact', path: '/contact' },
];

const footerServices = [
  { label: 'Landscaping', path: '/services/landscaping' },
  { label: 'Interior Works', path: '/services/interior-works' },
  { label: 'UPVC Windows', path: '/services/upvc-windows' },
  { label: 'Civil Waterproofing', path: '/services/civil-waterproofing' },
  { label: 'Deep Cleaning', path: '/services/deep-cleaning' },
  { label: 'Flooring & Wall Paneling', path: '/services/flooring-wall-paneling' },
  { label: 'Swimming Pool & Fountain', path: '/services/swimming-pool-fountain' },
  { label: 'WPC Paneling', path: '/services' },
  { label: 'Branding & Signage', path: '/services' },
  { label: 'SS & MS Fabrication', path: '/services' },
  { label: 'Electrical & Plumbing', path: '/services' },
  { label: 'CCTV Surveillance', path: '/services#cctv-surveillance' },
  { label: 'PVC & Bamboo Blinds', path: '/services#pvc-bamboo-blinds' },
  { label: 'Annual Maintenance', path: '/services#amc' },
];

export default function Footer() {
  const { ref, isVisible } = useScrollAnimation(0.05);

  return (
    <footer
      ref={ref}
      className={`bg-primary-900 text-white transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      <div className="w-full px-4 sm:px-6 lg:px-10 py-12 md:py-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            <div className="sm:col-span-2 lg:col-span-1">
              <Link to="/" className="flex items-center gap-2 mb-4 cursor-pointer">
                <img
                  src={companyInfo.logo}
                  alt="Infinite Consultant & Services"
                  className="h-10 w-auto"
                />
              </Link>
              <p className="text-sm text-white/70 leading-relaxed mb-4">
                {companyInfo.tagline}
              </p>

            </div>

            <div>
              <h4 className="font-heading font-semibold text-sm mb-4">Quick Links</h4>
              <ul className="space-y-2">
                {quickLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-sm text-white/70 hover:text-white transition-colors cursor-pointer"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-heading font-semibold text-sm mb-4">Our Services</h4>
              <ul className="space-y-2">
                {footerServices.map((service) => (
                  <li key={service.label}>
                    <Link
                      to={service.path}
                      className="text-sm text-white/70 hover:text-white transition-colors cursor-pointer"
                    >
                      {service.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-heading font-semibold text-sm mb-4">Contact</h4>
              <ul className="space-y-3">
                <li>
                  <a
                    href={`tel:${companyInfo.phone1.replace(/\s/g, '')}`}
                    className="flex items-start gap-2 text-sm text-white/70 hover:text-white transition-colors cursor-pointer"
                  >
                    <i className="ri-phone-line mt-0.5 shrink-0" />
                    <span>{companyInfo.phone1}</span>
                  </a>
                </li>
                <li>
                  <a
                    href={`tel:${companyInfo.phone2.replace(/\s/g, '')}`}
                    className="flex items-start gap-2 text-sm text-white/70 hover:text-white transition-colors cursor-pointer"
                  >
                    <i className="ri-phone-line mt-0.5 shrink-0" />
                    <span>{companyInfo.phone2}</span>
                  </a>
                </li>
                <li>
                  <a
                    href={getGenericWhatsAppUrl()}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="flex items-start gap-2 text-sm text-white/70 hover:text-white transition-colors cursor-pointer"
                  >
                    <i className="ri-whatsapp-line mt-0.5 shrink-0" />
                    <span>WhatsApp</span>
                  </a>
                </li>
                <li>
                  <a
                    href={`mailto:${companyInfo.email}`}
                    className="flex items-start gap-2 text-sm text-white/70 hover:text-white transition-colors break-all cursor-pointer"
                  >
                    <i className="ri-mail-line mt-0.5 shrink-0" />
                    <span>{companyInfo.email}</span>
                  </a>
                </li>
                <li className="flex items-start gap-2 text-sm text-white/70">
                  <i className="ri-map-pin-line mt-0.5 shrink-0" />
                  <span>{companyInfo.address}</span>
                </li>
                <li>
                  <a
                    href={companyInfo.facebook}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-all duration-300 hover:scale-110 cursor-pointer"
                  >
                    <i className="ri-facebook-fill" />
                    <span>Facebook</span>
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.instagram.com/infiniteconsultantandservices"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-all duration-300 hover:scale-110 cursor-pointer"
                  >
                    <i className="ri-instagram-line" />
                    <span>Instagram</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="w-full px-4 sm:px-6 lg:px-10 py-5">
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-white/50 text-center sm:text-left">
              Copyright &copy; 2025 {companyInfo.name}. All Rights Reserved.
            </p>
            <p className="text-xs text-white/50">
              Transforming Spaces, Enhancing Lives
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}